import type { AttributeSelection } from "@fero/pages";
import type { WithDbId } from "adl-gen/common/db";
import type {
  NominatedPurchaserDetailsView,
  StorageLocationView,
} from "adl-gen/ferovinum/app/api";
import type {
  Product,
  PurchaseRequestSalePriceType,
} from "adl-gen/ferovinum/app/db";
import { create } from "zustand";

export interface SelectedProduct {
  productId: string;
  summary: WithDbId<Product>;
  salePrice: number;
  quantity: number;
  subtotal: number;
  available: number;
  producible: number;
  lineItemDiscount: number;
  freeUnits: number;
  attributeSelection: AttributeSelection;
}

interface TradeSaleOptions {
  nominatedPurchaser?: NominatedPurchaserDetailsView;
  storageLocation?: StorageLocationView;
  salePriceType?: PurchaseRequestSalePriceType;
  /**
   * Used to indicate if the wizard is in edit (amend) mode.
   */
  isEditMode?: boolean;
}

export interface TradeSalesStore {
  purchaseRequestId: string | undefined;
  setPurchaseRequestId: (purchaseRequestId?: string) => void;
  reservedId: string | undefined;
  releasedId: string | undefined;
  setReservedId: (reservedId?: string) => void;
  setReleasedId: (releasedId?: string) => void;
  clearEditState: () => void;
  // A list of products that is currently selected in the users browse modal
  browsingProducts: SelectedProduct[];
  setBrowsingProducts: (products: SelectedProduct[]) => void;
  addBrowsingProduct: (product: SelectedProduct) => void;
  removeBrowsingProduct: (product: SelectedProduct) => void;

  options: TradeSaleOptions;
  updateOptions: (options: TradeSaleOptions) => void;
}

export const useTradeSalesStore = create<TradeSalesStore>((set) => ({
  purchaseRequestId: undefined,
  setPurchaseRequestId: (purchaseRequestId?: string) =>
    set({ purchaseRequestId }),
  reservedId: undefined,
  setReservedId: (reservedId?: string) => set({ reservedId }),
  releasedId: undefined,
  setReleasedId: (releasedId?: string) => set({ releasedId }),
  clearEditState: () =>
    set({
      purchaseRequestId: undefined,
      reservedId: undefined,
      releasedId: undefined,
    }),
  browsingProducts: [],
  setBrowsingProducts: (products) => {
    set({ browsingProducts: products });
  },
  addBrowsingProduct: (product: SelectedProduct) => {
    set((state) =>
      state.browsingProducts.find(
        (selected) => selected.productId === product.productId,
      )
        ? state
        : { browsingProducts: [...state.browsingProducts, product] },
    );
  },
  removeBrowsingProduct: (product: SelectedProduct) => {
    set((state) => ({
      browsingProducts: state.browsingProducts.filter(
        (p) => p.productId !== product.productId,
      ),
    }));
  },

  options: {},
  updateOptions: (options) =>
    set((state) => ({ options: { ...state.options, ...options } })),
}));
